import { Check, EllipsisVertical } from 'lucide-react';
import { DropdownMenu as RadixMenu } from 'radix-ui';
import { Fragment, forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { IconButton, type IconButtonSize, type IconButtonVariant } from './Button';

/** Root (Radix DropdownMenu): arrow keys, typeahead, Escape, RTL-aware sub-menus. */
export const DropdownMenu = RadixMenu.Root;
export const DropdownMenuTrigger = RadixMenu.Trigger;
export const DropdownMenuGroup = RadixMenu.Group;
export const DropdownMenuRadioGroup = RadixMenu.RadioGroup;

const MENU_SURFACE =
  'z-[60] flex min-w-[220px] flex-col gap-0.5 rounded-[10px] border border-border bg-surface p-1.5 text-13 text-text shadow-lg outline-none data-[state=open]:animate-pop-in';

const ITEM =
  'relative flex h-[34px] cursor-pointer select-none items-center gap-2 rounded-[6px] px-3 text-start outline-none data-[highlighted]:bg-surface-2 data-[disabled]:cursor-not-allowed data-[disabled]:text-text-3';

export const DropdownMenuContent = forwardRef<
  ElementRef<typeof RadixMenu.Content>,
  ComponentPropsWithoutRef<typeof RadixMenu.Content>
>(function DropdownMenuContent(
  { className, sideOffset = 6, align = 'end', collisionPadding = 8, ...rest },
  ref,
) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        className={cn(MENU_SURFACE, className)}
        {...rest}
      />
    </RadixMenu.Portal>
  );
});

export interface DropdownMenuItemProps extends ComponentPropsWithoutRef<typeof RadixMenu.Item> {
  /** `danger` renders the item in error-text (Archive, Close session…, Retire). */
  tone?: 'default' | 'danger';
  icon?: ReactNode;
  /** Muted trailing text, e.g. a size or a shortcut. */
  meta?: ReactNode;
}

export const DropdownMenuItem = forwardRef<ElementRef<typeof RadixMenu.Item>, DropdownMenuItemProps>(
  function DropdownMenuItem({ className, tone = 'default', icon, meta, children, ...rest }, ref) {
    return (
      <RadixMenu.Item
        ref={ref}
        className={cn(ITEM, tone === 'danger' && 'text-error-text', className)}
        {...rest}
      >
        {icon ? (
          <span aria-hidden className="inline-flex shrink-0 [&>svg]:size-3.5">
            {icon}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {meta ? <span className="shrink-0 text-11 text-text-3">{meta}</span> : null}
      </RadixMenu.Item>
    );
  },
);

export const DropdownMenuCheckboxItem = forwardRef<
  ElementRef<typeof RadixMenu.CheckboxItem>,
  ComponentPropsWithoutRef<typeof RadixMenu.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...rest }, ref) {
  return (
    <RadixMenu.CheckboxItem ref={ref} className={cn(ITEM, className)} {...rest}>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <RadixMenu.ItemIndicator>
        <Check aria-hidden className="size-3.5 text-primary-text" strokeWidth={2.5} />
      </RadixMenu.ItemIndicator>
    </RadixMenu.CheckboxItem>
  );
});

export const DropdownMenuRadioItem = forwardRef<
  ElementRef<typeof RadixMenu.RadioItem>,
  ComponentPropsWithoutRef<typeof RadixMenu.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...rest }, ref) {
  return (
    <RadixMenu.RadioItem
      ref={ref}
      className={cn(
        ITEM,
        'data-[state=checked]:bg-primary-soft data-[state=checked]:font-medium data-[state=checked]:text-primary-text',
        className,
      )}
      {...rest}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <RadixMenu.ItemIndicator>
        <Check aria-hidden className="size-3.5" strokeWidth={2.5} />
      </RadixMenu.ItemIndicator>
    </RadixMenu.RadioItem>
  );
});

export const DropdownMenuLabel = forwardRef<
  ElementRef<typeof RadixMenu.Label>,
  ComponentPropsWithoutRef<typeof RadixMenu.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return (
    <RadixMenu.Label
      ref={ref}
      className={cn('px-3 pt-1.5 pb-1 text-11 font-semibold text-text-3', className)}
      {...rest}
    />
  );
});

export const DropdownMenuSeparator = forwardRef<
  ElementRef<typeof RadixMenu.Separator>,
  ComponentPropsWithoutRef<typeof RadixMenu.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return (
    <RadixMenu.Separator ref={ref} className={cn('my-1 h-px shrink-0 bg-border-2', className)} {...rest} />
  );
});

/* ------------------------------------------------------------------------------------------------
 * KebabMenu — ⋮ trigger + item list
 * ---------------------------------------------------------------------------------------------- */

export interface KebabMenuItem {
  label: ReactNode;
  onSelect?: () => void;
  tone?: 'default' | 'danger';
  icon?: ReactNode;
  meta?: ReactNode;
  disabled?: boolean;
  /** Draw a separator above this item. */
  separatorBefore?: boolean;
}

export interface KebabMenuProps {
  items: KebabMenuItem[];
  /** Accessible name of the trigger (default "More actions"). */
  label?: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Icon size (default 16, as in the design's kebabs). */
  iconSize?: 12 | 14 | 16 | 18;
  align?: 'start' | 'center' | 'end';
  className?: string;
  contentClassName?: string;
}

/** "More actions" (⋮) menu used in page headers and table rows. */
export function KebabMenu({
  items,
  label = 'More actions',
  variant = 'secondary',
  size = 'md',
  iconSize = 16,
  align = 'end',
  className,
  contentClassName,
}: KebabMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          aria-label={label}
          variant={variant}
          size={size}
          iconSize={iconSize}
          className={className}
        >
          <EllipsisVertical strokeWidth={2} />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={contentClassName}>
        {items.map((item, index) => (
          <Fragment key={index}>
            {item.separatorBefore ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              tone={item.tone}
              icon={item.icon}
              meta={item.meta}
              disabled={item.disabled}
              onSelect={item.onSelect}
            >
              {item.label}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
